import { useEffect, useState } from "react";
import { Plus, PencilLine, Power, Star, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { EmptyState } from "../../components/shared/EmptyState";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiError } from "../../lib/api";

const PLACEMENTS = [
  { id: "accueil", label: "Accueil" },
  { id: "coordination", label: "Coordination" },
  { id: "realisations", label: "Réalisations" },
];
const SOURCES = ["Google", "Mariages.net", "Direct"];
const EMPTY = { author: "", text: "", rating: 5, source: "Google", placements: [], event_context: "" };

export default function ReviewsAdminPage() {
  const [reviews, setReviews] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => setReviews([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ author: r.author, text: r.text, rating: r.rating, source: r.source || "Google", placements: r.placements || [], event_context: r.event_context || "" });
    setOpen(true);
  };

  const togglePlacement = (p) => setForm((f) => ({ ...f, placements: f.placements.includes(p) ? f.placements.filter((x) => x !== p) : [...f.placements, p] }));

  const submit = async () => {
    try {
      if (editing) { await api.patch(`/admin/reviews/${editing.id}`, form); toast.success("Avis mis à jour"); }
      else { await api.post("/admin/reviews", form); toast.success("Avis créé — visible selon les placements cochés"); }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async (r) => {
    try { await api.post(`/admin/reviews/${r.id}/toggle`); toast.success(r.active ? "Avis masqué du site" : "Avis publié"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!reviews) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="reviews-loading">Chargement…</div>;

  return (
    <div data-testid="admin-reviews-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">Avis clients</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Avis clients</h1>
          <p className="mt-1 text-sm text-muted-foreground">Témoignages réels (Google, Mariages.net, direct) placés librement sur Accueil, Coordination et Réalisations. Note moyenne exposée en Schema.org (AggregateRating) pour le SEO.</p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid="review-create">
          <Plus className="h-4 w-4" /> Nouvel avis
        </button>
      </div>

      {reviews.length === 0 ? (
        <div className="mt-6"><EmptyState icon={MessageSquareQuote} title="Aucun avis">Ajoutez le premier témoignage client.</EmptyState></div>
      ) : (
        <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="reviews-grid">
          {reviews.map((r) => (
            <div key={r.id} className="card-ae p-5" data-testid={`review-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-deepgreen">{r.author}</p>
                  <div className="mt-1 flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`h-4 w-4 ${i < r.rating ? "fill-gold text-gold" : "text-border"}`} />
                    ))}
                    <span className="ml-2 rounded-full bg-sage-light px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-deepgreen">{r.source}</span>
                  </div>
                </div>
                <StatusBadge status={r.active ? "Actif" : "Archivé"} />
              </div>
              <p className="mt-3 text-sm italic leading-relaxed text-foreground/80">« {r.text} »</p>
              {r.event_context && <p className="mt-2 text-xs text-muted-foreground">{r.event_context}</p>}
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex gap-1.5">
                  {(r.placements || []).map((p) => (
                    <span key={p} className="rounded-full border border-sage px-2.5 py-0.5 text-[10px] font-semibold text-deepgreen">{PLACEMENTS.find((x) => x.id === p)?.label || p}</span>
                  ))}
                  {(r.placements || []).length === 0 && <span className="text-[10px] text-muted-foreground">Non placé</span>}
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => openEdit(r)} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`review-edit-${r.id}`}><PencilLine className="h-3.5 w-3.5" /></button>
                  <button type="button" onClick={() => toggle(r)} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`review-toggle-${r.id}`}>
                    <Power className="mr-1 inline h-3.5 w-3.5" />{r.active ? "Masquer" : "Publier"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-cream sm:max-w-xl" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-deepgreen">{editing ? "Modifier l'avis" : "Nouvel avis"}</DialogTitle>
            <DialogDescription>Saisie manuelle d'un témoignage réel — jamais de faux avis.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="label-ae" htmlFor="r-author">Auteur</label><input id="r-author" className="input-ae" placeholder="ex : Julie & Clément" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="review-author" /></div>
              <div>
                <label className="label-ae" htmlFor="r-source">Source</label>
                <select id="r-source" className="input-ae" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="review-source">
                  {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="label-ae">Note</label>
                <div className="flex gap-1 pt-1.5" data-testid="review-rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })} data-testid={`review-star-${n}`}>
                      <Star className={`h-6 w-6 ${n <= form.rating ? "fill-gold text-gold" : "text-border"}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div><label className="label-ae" htmlFor="r-context">Contexte (facultatif)</label><input id="r-context" className="input-ae" placeholder="ex : Mariage au Moulin d'Artres — août 2026" value={form.event_context} onChange={(e) => setForm({ ...form, event_context: e.target.value })} data-testid="review-context" /></div>
            </div>
            <div><label className="label-ae" htmlFor="r-text">Témoignage</label><textarea id="r-text" className="input-ae min-h-24" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="review-text" /></div>
            <div>
              <p className="label-ae">Placements sur le site</p>
              <div className="flex gap-2" data-testid="review-placements">
                {PLACEMENTS.map((p) => (
                  <button key={p.id} type="button" onClick={() => togglePlacement(p.id)} data-testid={`review-placement-${p.id}`}
                    className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${form.placements.includes(p.id) ? "border-deepgreen bg-sage-light text-deepgreen" : "border-border bg-white text-muted-foreground hover:border-deepgreen"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <button type="button" onClick={submit} disabled={form.author.length < 2 || form.text.length < 10} className="btn-primary-ae modal-sticky-btn w-full disabled:opacity-50" data-testid="review-submit">
              {editing ? "Enregistrer" : "Créer l'avis"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
